import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/CreateBlog.css";

function CreateBlog() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    image: "",
    excerpt: "",
    content: "",
    tags: "",
    author: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const API_BASE = "https://api.niveshly.com";

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/blogs`);
      const data = await response.json();
      setBlogs(data);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const url = editingId
        ? `${API_BASE}/blogs/${editingId}`
        : `${API_BASE}/blogs`;

      const method = editingId ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage(editingId ? "Blog updated successfully!" : "Blog created successfully!");
        resetForm();
        fetchBlogs();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("Error saving blog. Please try again.");
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title,
      image: blog.image,
      excerpt: blog.excerpt,
      content: blog.content,
      tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : blog.tags,
      author: blog.author
    });
    setEditingId(blog.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    try {
      const response = await fetch(`${API_BASE}/blogs/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setMessage("Blog deleted successfully!");
        fetchBlogs();
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage("Error deleting blog: " + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      image: "",
      excerpt: "",
      content: "",
      tags: "",
      author: ""
    });
    setEditingId(null);
  };
  const handleHTMLPaste = (e, fieldName) => {
    e.preventDefault();

    const clipboardData = e.clipboardData;
    const html = clipboardData.getData("text/html");
    const text = clipboardData.getData("text/plain");

    let finalHTML = "";

    if (html) {
      finalHTML = html;
    } else {
      finalHTML = text
        .split("\n")
        .filter(line => line.trim() !== "")
        .map(line => `<p>${line}</p>`)
        .join("");
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || "") + finalHTML,
    }));
  };


  return (
    <div className="create-blog-page">
      <div className="create-blog-container">
        {/* Header */}
        <div className="page-header">
          <h1>📝 Article Management</h1>
          <button className="view-blogs-btn" onClick={() => navigate("/blogs")}>
            View All Article
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`message ${message.includes("Error") ? "error" : "success"}`}>
            {message}
          </div>
        )}

        {/* Form */}
        <form className="blog-form" onSubmit={handleSubmit}>
          <h2>{editingId ? "Edit Article" : "Create Article"}</h2>

          <div className="form-group">
            <label htmlFor="title">Article Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter Article title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="image">Featured Image URL *</label>
            <input
              type="url"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              required
            />
            {formData.image && (
              <div className="image-preview">
                <img src={formData.image} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Excerpt / Summary *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Brief summary of the blog (2-3 sentences)"
              rows="3"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Full Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              onPaste={(e) => handleHTMLPaste(e, "content")}
              placeholder="Paste anything — it auto converts to HTML"
              rows="12"
              required
            />

            <small>Tip: Use HTML tags like &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;ul&gt;, etc.</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Investment, IPO, Market Analysis"
              />
            </div>

            <div className="form-group">
              <label htmlFor="author">Author</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
              />
            </div>
          </div>

          <div className="form-actions">
            {editingId && (
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Saving..." : editingId ? "Update Blog" : "Create Blog"}
            </button>
          </div>
        </form>

        {/* Existing Blogs List */}
        <div className="blogs-list">
          <h2>📚 Existing Article ({blogs.length})</h2>

          {blogs.length === 0 ? (
            <p className="no-blogs">No Article yet. Create your first Article above!</p>
          ) : (
            <div className="blogs-table">
              {blogs.map((blog) => (
                <div key={blog.id} className="blog-item">
                  <div className="blog-item-image">
                    <img src={blog.image} alt={blog.title} />
                  </div>
                  <div className="blog-item-content">
                    <h3>{blog.title}</h3>
                    <p className="blog-item-excerpt">{blog.excerpt}</p>
                    <div className="blog-item-meta">
                      <span>By {blog.author}</span>
                      <span>•</span>
                      <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="blog-item-actions">
                    <button className="edit-btn" onClick={() => handleEdit(blog)}>
                      ✏️ Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(blog.id)}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateBlog;